<script lang="ts">
  import { onMount } from 'svelte';
  import { invoicesStorage, zReportsStorage, settingsStorage } from '../services/storage';
  import { frappeClient } from '../services/frappe';
  import { formatCurrency, formatDate } from '../utils/format';
  import { printToBluetoothPrinter } from '../utils/printer';
  import type { SalesInvoice, ZReport, ZReportSalesEntry } from '../types';

  let invoices = $state<SalesInvoice[]>([]);
  let lastZReport = $state<ZReport | null>(null);
  let pendingInvoices = $state<number>(0);
  let syncedInvoices = $state<number>(0);
  let pendingZReports = $state<number>(0);
  let showZReportPreview = $state(false);
  let showZReportCreate = $state(false);
  let isCreatingZReport = $state(false);
  let zReportPreview = $state<Partial<ZReport> | null>(null);

  onMount(() => {
    loadData();
  });

  function loadData() {
    // Lade letzten Z-Bericht
    lastZReport = zReportsStorage.getLast();

    // Lade alle Rechnungen seit letztem Z-Bericht
    if (lastZReport?.end_time) {
      invoices = invoicesStorage.getSince(lastZReport.end_time);
    } else {
      // Kein Z-Bericht vorhanden, lade alle Rechnungen
      invoices = invoicesStorage.load();
    }

    // Berechne Sync-Status
    const allInvoices = invoicesStorage.load();
    syncedInvoices = allInvoices.filter((inv) => inv.synced).length;
    pendingInvoices = allInvoices.filter((inv) => !inv.synced).length;

    const allZReports = zReportsStorage.load();
    pendingZReports = allZReports.filter((r) => !r.synced).length;
  }

  function calculateZReportPreview(): Partial<ZReport> {
    const relevantInvoices = invoices;

    let cashSales = 0;
    let cardSales = 0;
    let sumupSales = 0;
    let totalSales = 0;
    const sales: ZReportSalesEntry[] = [];
    let minInvoiceDate: Date | null = null;

    relevantInvoices.forEach((inv) => {
      // Sammle Rechnungsnummer und Frappe Name
      const invoiceNumber = inv.invoice_number || inv.id?.substring(0, 8) || 'N/A';
      const frappeName = inv.frappe_name || '';

      // Finde das kleinste invoice_created Datum (start_time)
      if (inv.created_at) {
        const invoiceDate = new Date(inv.created_at);
        if (!minInvoiceDate || invoiceDate < minInvoiceDate) {
          minInvoiceDate = invoiceDate;
        }
      }

      // Erstelle Sales Entry für Child Table
      if (frappeName) {
        // Prüfe ob bereits vorhanden (anhand frappe_name)
        const exists = sales.some((entry) => entry.sales_invoice === frappeName);
        if (!exists) {
          sales.push({
            sales_invoice: frappeName,
            sales_pos_invoice_number: invoiceNumber
          });
        }
      }

      inv.payments.forEach((payment) => {
        if (payment.type === 'cash') {
          cashSales += payment.amount;
        } else if (payment.type === 'card') {
          cardSales += payment.amount;
        } else if (payment.type === 'sumup') {
          // SumUp-Verkäufe werden zu Kartenverkäufen hinzugezählt
          sumupSales += payment.amount;
          cardSales += payment.amount;
        }
        totalSales += payment.amount;
      });
    });

    // start_time ist das kleinste invoice_created Datum der enthaltenen Rechnungen
    // Falls keine Rechnungen vorhanden, verwende end_time des letzten Z-Berichts oder aktuelles Datum
    const startTime: string = minInvoiceDate
      ? (minInvoiceDate as Date).toISOString()
      : lastZReport?.end_time || new Date().toISOString();

    return {
      sales_count: relevantInvoices.length,
      total_sales: totalSales,
      cash_sales: cashSales,
      card_sales: cardSales,
      sumup_sales: sumupSales,
      sales: sales,
      start_time: startTime,
      end_time: new Date().toISOString()
    };
  }

  function showPreview() {
    // Prüfe ob alle Invoices synchronisiert sind
    const unsyncedInvoices = invoices.filter((inv) => !inv.synced);
    if (unsyncedInvoices.length > 0) {
      alert(
        `Bitte warten Sie, bis alle Rechnungen synchronisiert sind.\n\nNoch ${unsyncedInvoices.length} Rechnung(en) ausstehend.`
      );
      return;
    }

    zReportPreview = calculateZReportPreview();
    showZReportPreview = true;
  }

  async function createZReport() {
    // Prüfe ob alle Invoices synchronisiert sind
    const unsyncedInvoices = invoices.filter((inv) => !inv.synced);
    if (unsyncedInvoices.length > 0) {
      alert(
        `Z-Bericht kann nicht erstellt werden.\n\nNoch ${unsyncedInvoices.length} Rechnung(en) müssen synchronisiert werden.`
      );
      isCreatingZReport = false;
      return;
    }
    isCreatingZReport = true;

    try {
      const preview = calculateZReportPreview();

      // Lade POS-ID aus Settings
      const settings = settingsStorage.load();
      const posId = settings.pos_id || '01';

      // Z-Report ohne ID erstellen (ID wird vom Server vergeben)
      const zReport: ZReport = {
        report_number: `Z-${new Date().toISOString().split('T')[0].replace(/-/g, '')}-${Date.now().toString().slice(-6)}`,
        start_time: preview.start_time || new Date(0).toISOString(),
        end_time: preview.end_time || new Date().toISOString(),
        sales_count: preview.sales_count || 0,
        total_sales: preview.total_sales || 0,
        cash_sales: preview.cash_sales || 0,
        card_sales: preview.card_sales || 0,
        sumup_sales: preview.sumup_sales || 0,
        sales: preview.sales || [],
        pos_id: posId,
        status: 'submitted',
        synced: false
      };

      // Versuche zu synchronisieren (falls online)
      if (navigator.onLine) {
        try {
          // Server erstellt die ID
          const settings = settingsStorage.load();
          const response = await frappeClient.syncZReport(zReport, settings.test_mode || false);
          const frappeName = response.name; // Server-generierte ID

          // Speichere mit Server-ID
          zReportsStorage.add({
            ...zReport,
            id: frappeName,
            synced: true,
            synced_at: new Date().toISOString()
          });
        } catch (syncError) {
          // Bei Fehler: Speichere lokal mit temporärer ID für späteren Retry
          const tempId = `temp_zreport_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          zReportsStorage.add({
            ...zReport,
            id: tempId
          });
          console.warn('Z-Report Sync fehlgeschlagen, wird später wiederholt:', syncError);
        }
      } else {
        // Offline: Speichere mit temporärer ID
        const tempId = `temp_zreport_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        zReportsStorage.add({
          ...zReport,
          id: tempId
        });
      }

      // Lade Daten neu
      loadData();
      showZReportCreate = false;
      showZReportPreview = false;
    } catch (error) {
      console.error('Fehler beim Erstellen des Z-Berichts:', error);
      alert(
        'Fehler beim Erstellen des Z-Berichts: ' +
          (error instanceof Error ? error.message : 'Unbekannter Fehler')
      );
    } finally {
      isCreatingZReport = false;
    }
  }

  function getPaymentTypeLabel(type: string): string {
    switch (type) {
      case 'cash':
        return 'Bar';
      case 'card':
        return 'Karte';
      case 'sumup':
        return 'SumUp';
      default:
        return type;
    }
  }

  async function showReceipt(invoice: SalesInvoice) {
    try {
      await printToBluetoothPrinter(invoice);
    } catch (error) {
      console.error('Fehler beim Anzeigen des Belegs:', error);
      alert(
        'Fehler beim Anzeigen des Belegs: ' +
          (error instanceof Error ? error.message : 'Unbekannter Fehler')
      );
    }
  }
</script>

<div class="min-h-screen bg-gray-50 p-4 md:p-6">
  <div class="max-w-7xl mx-auto">
    <!-- Header -->
    <div class="mb-6">
      <h1 class="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Management</h1>
      <p class="text-gray-600">Übersicht über Zahlungen, Sync-Status und Z-Berichte</p>
    </div>

    <!-- Sync Status Cards -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <!-- Synced Invoices -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-600 mb-1">Gesynct</p>
            <p class="text-2xl font-bold text-green-600">{syncedInvoices}</p>
            <p class="text-xs text-gray-500 mt-1">Rechnungen</p>
          </div>
          <div class="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
            <svg
              class="w-6 h-6 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        </div>
      </div>

      <!-- Pending Invoices -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-600 mb-1">Ausstehend</p>
            <p class="text-2xl font-bold text-yellow-600">{pendingInvoices}</p>
            <p class="text-xs text-gray-500 mt-1">Rechnungen</p>
          </div>
          <div class="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
            <svg
              class="w-6 h-6 text-yellow-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
        </div>
      </div>

      <!-- Pending Z-Reports -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-600 mb-1">Z-Berichte</p>
            <p class="text-2xl font-bold text-blue-600">{pendingZReports}</p>
            <p class="text-xs text-gray-500 mt-1">Ausstehend</p>
          </div>
          <div class="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
            <svg
              class="w-6 h-6 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
        </div>
      </div>
    </div>

    <!-- Z-Bericht Actions -->
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
      <div class="flex items-center justify-between">
        <div>
          <h2 class="text-lg font-semibold text-gray-900 mb-1">Z-Bericht</h2>
          {#if lastZReport}
            <p class="text-sm text-gray-600">
              Letzter Z-Bericht: {formatDate(lastZReport.end_time, 'long')}
            </p>
          {:else}
            <p class="text-sm text-gray-600">Noch kein Z-Bericht erstellt</p>
          {/if}
        </div>
        <div class="flex gap-2">
          <button
            onclick={showPreview}
            class="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium text-sm touch-manipulation"
          >
            Vorschau
          </button>
          <button
            onclick={() => {
              // Prüfe ob alle Invoices synchronisiert sind
              const unsyncedInvoices = invoices.filter((inv) => !inv.synced);
              if (unsyncedInvoices.length > 0) {
                alert(
                  `Z-Bericht kann nicht erstellt werden.\n\nNoch ${unsyncedInvoices.length} Rechnung(en) müssen synchronisiert werden.`
                );
                return;
              }
              showZReportCreate = true;
              zReportPreview = calculateZReportPreview();
            }}
            class="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium text-sm touch-manipulation"
          >
            Z-Bericht erstellen
          </button>
        </div>
      </div>
    </div>

    <!-- Zahlungen seit letztem Z-Bericht -->
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6">
      <div class="flex items-center justify-between mb-4">
        <h2 class="text-lg font-semibold text-gray-900">Zahlungen seit letztem Z-Bericht</h2>
        <span class="text-sm text-gray-600">
          {invoices.length}
          {invoices.length === 1 ? 'Rechnung' : 'Rechnungen'}
        </span>
      </div>

      {#if invoices.length === 0}
        <div class="text-center py-8">
          <p class="text-gray-500">Keine Zahlungen seit dem letzten Z-Bericht</p>
        </div>
      {:else}
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead>
              <tr class="border-b border-gray-200">
                <th class="text-left py-3 px-4 text-sm font-semibold text-gray-700">Datum</th>
                <th class="text-left py-3 px-4 text-sm font-semibold text-gray-700">Beleg-Nr.</th>
                <th class="text-left py-3 px-4 text-sm font-semibold text-gray-700">Frappe Name</th>
                <th class="text-right py-3 px-4 text-sm font-semibold text-gray-700">Betrag</th>
                <th class="text-left py-3 px-4 text-sm font-semibold text-gray-700">Zahlung</th>
                <th class="text-center py-3 px-4 text-sm font-semibold text-gray-700">Status</th>
                <th class="text-center py-3 px-4 text-sm font-semibold text-gray-700">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {#each invoices as invoice}
                <tr class="border-b border-gray-100 hover:bg-gray-50">
                  <td class="py-3 px-4 text-sm text-gray-900">
                    {formatDate(invoice.created_at, 'short')}
                  </td>
                  <td class="py-3 px-4 text-sm text-gray-900">
                    {invoice.invoice_number || invoice.id?.substring(0, 8) || 'N/A'}
                  </td>
                  <td class="py-3 px-4 text-sm text-gray-600 font-mono">
                    {invoice.frappe_name || '-'}
                  </td>
                  <td class="py-3 px-4 text-sm text-right font-medium text-gray-900">
                    {formatCurrency(invoice.total)}
                  </td>
                  <td class="py-3 px-4 text-sm text-gray-600">
                    {#each invoice.payments as payment}
                      <span class="inline-block mr-2">
                        {getPaymentTypeLabel(payment.type)}: {formatCurrency(payment.amount)}
                      </span>
                    {/each}
                  </td>
                  <td class="py-3 px-4 text-center">
                    {#if invoice.synced}
                      <span
                        class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                      >
                        Gesynct
                      </span>
                    {:else}
                      <span
                        class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800"
                      >
                        Ausstehend
                      </span>
                    {/if}
                  </td>
                  <td class="py-3 px-4 text-center">
                    <button
                      onclick={() => showReceipt(invoice)}
                      class="inline-flex items-center px-3 py-1.5 text-xs font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors touch-manipulation"
                      aria-label="Beleg anzeigen"
                    >
                      <svg
                        class="w-4 h-4 mr-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                      Beleg
                    </button>
                  </td>
                </tr>
              {/each}
            </tbody>
            <tfoot>
              <tr class="bg-gray-50">
                <td colspan="3" class="py-3 px-4 text-sm font-semibold text-gray-900"> Gesamt </td>
                <td class="py-3 px-4 text-sm font-bold text-right text-primary-600">
                  {formatCurrency(invoices.reduce((sum, inv) => sum + inv.total, 0))}
                </td>
                <td colspan="3"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      {/if}
    </div>
  </div>
</div>

<!-- Z-Bericht Vorschau Modal -->
{#if showZReportPreview && zReportPreview}
  <div
    class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
    onclick={() => (showZReportPreview = false)}
    role="button"
    tabindex="0"
    onkeypress={(e) => e.key === 'Escape' && (showZReportPreview = false)}
  >
    <div
      class="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6"
      onclick={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
      tabindex="0"
    >
      <div class="flex items-center justify-between mb-4">
        <h2 class="text-xl font-bold text-gray-900">Z-Bericht Vorschau</h2>
        <button
          onclick={() => (showZReportPreview = false)}
          class="text-gray-500 hover:text-gray-700"
          aria-label="Schließen"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <div class="space-y-4">
        <div class="grid grid-cols-2 gap-4">
          <div>
            <p class="text-sm text-gray-600 mb-1">Von</p>
            <p class="font-medium text-gray-900">
              {formatDate(zReportPreview.start_time || '', 'long')}
            </p>
          </div>
          <div>
            <p class="text-sm text-gray-600 mb-1">Bis</p>
            <p class="font-medium text-gray-900">
              {formatDate(zReportPreview.end_time || '', 'long')}
            </p>
          </div>
        </div>

        <div class="border-t border-gray-200 pt-4">
          <div class="space-y-3">
            <div class="flex justify-between">
              <span class="text-gray-600">Anzahl Verkäufe:</span>
              <span class="font-medium text-gray-900">{zReportPreview.sales_count || 0}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">Bar:</span>
              <span class="font-medium text-gray-900"
                >{formatCurrency(zReportPreview.cash_sales || 0)}</span
              >
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">Karte (inkl. SumUp):</span>
              <span class="font-medium text-gray-900"
                >{formatCurrency(zReportPreview.card_sales || 0)}</span
              >
            </div>
            {#if zReportPreview.sumup_sales && zReportPreview.sumup_sales > 0}
              <div class="flex justify-between text-sm text-gray-500 pl-4">
                <span>davon SumUp:</span>
                <span>{formatCurrency(zReportPreview.sumup_sales || 0)}</span>
              </div>
            {/if}
            {#if zReportPreview.sales && zReportPreview.sales.length > 0}
              <div class="pt-3 border-t border-gray-200">
                <p class="text-sm text-gray-600 mb-2">
                  Enthaltene Rechnungen ({zReportPreview.sales.length}):
                </p>
                <div class="max-h-32 overflow-y-auto bg-gray-50 rounded p-2">
                  <div class="flex flex-wrap gap-1">
                    {#each zReportPreview.sales as salesEntry}
                      <span
                        class="inline-block px-2 py-1 text-xs font-mono bg-white text-gray-900 rounded border border-gray-200"
                        title={salesEntry.sales_invoice || salesEntry.sales_pos_invoice_number}
                      >
                        {salesEntry.sales_pos_invoice_number}{#if salesEntry.sales_invoice}
                          ({salesEntry.sales_invoice}){/if}
                      </span>
                    {/each}
                  </div>
                </div>
              </div>
            {/if}
            <div class="flex justify-between pt-3 border-t border-gray-200">
              <span class="text-lg font-semibold text-gray-900">Gesamt:</span>
              <span class="text-lg font-bold text-primary-600">
                {formatCurrency(zReportPreview.total_sales || 0)}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="flex justify-end gap-2 mt-6">
        <button
          onclick={() => (showZReportPreview = false)}
          class="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
        >
          Schließen
        </button>
      </div>
    </div>
  </div>
{/if}

<!-- Z-Bericht Erstellen Modal -->
{#if showZReportCreate && zReportPreview}
  <div
    class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
    onclick={() => (showZReportCreate = false)}
    role="button"
    tabindex="0"
    onkeypress={(e) => e.key === 'Escape' && (showZReportCreate = false)}
  >
    <div
      class="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6"
      onclick={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
      tabindex="0"
    >
      <div class="flex items-center justify-between mb-4">
        <h2 class="text-xl font-bold text-gray-900">Z-Bericht erstellen</h2>
        <button
          onclick={() => (showZReportCreate = false)}
          class="text-gray-500 hover:text-gray-700"
          aria-label="Schließen"
          disabled={isCreatingZReport}
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <div class="space-y-4 mb-6">
        <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p class="text-sm text-yellow-800">
            <strong>Warnung:</strong> Nach dem Erstellen des Z-Berichts werden alle Zahlungen seit dem
            letzten Z-Bericht als abgeschlossen markiert.
          </p>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <p class="text-sm text-gray-600 mb-1">Von</p>
            <p class="font-medium text-gray-900">
              {formatDate(zReportPreview.start_time || '', 'long')}
            </p>
          </div>
          <div>
            <p class="text-sm text-gray-600 mb-1">Bis</p>
            <p class="font-medium text-gray-900">
              {formatDate(zReportPreview.end_time || '', 'long')}
            </p>
          </div>
        </div>

        <div class="border-t border-gray-200 pt-4">
          <div class="space-y-3">
            <div class="flex justify-between">
              <span class="text-gray-600">Anzahl Verkäufe:</span>
              <span class="font-medium text-gray-900">{zReportPreview.sales_count || 0}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">Bar:</span>
              <span class="font-medium text-gray-900"
                >{formatCurrency(zReportPreview.cash_sales || 0)}</span
              >
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">Karte (inkl. SumUp):</span>
              <span class="font-medium text-gray-900"
                >{formatCurrency(zReportPreview.card_sales || 0)}</span
              >
            </div>
            {#if zReportPreview.sumup_sales && zReportPreview.sumup_sales > 0}
              <div class="flex justify-between text-sm text-gray-500 pl-4">
                <span>davon SumUp:</span>
                <span>{formatCurrency(zReportPreview.sumup_sales || 0)}</span>
              </div>
            {/if}
            {#if zReportPreview.sales && zReportPreview.sales.length > 0}
              <div class="pt-3 border-t border-gray-200">
                <p class="text-sm text-gray-600 mb-2">
                  Enthaltene Rechnungen ({zReportPreview.sales.length}):
                </p>
                <div class="max-h-32 overflow-y-auto bg-gray-50 rounded p-2">
                  <div class="flex flex-wrap gap-1">
                    {#each zReportPreview.sales as salesEntry}
                      <span
                        class="inline-block px-2 py-1 text-xs font-mono bg-white text-gray-900 rounded border border-gray-200"
                        title={salesEntry.sales_invoice || salesEntry.sales_pos_invoice_number}
                      >
                        {salesEntry.sales_pos_invoice_number}{#if salesEntry.sales_invoice}
                          ({salesEntry.sales_invoice}){/if}
                      </span>
                    {/each}
                  </div>
                </div>
              </div>
            {/if}
            <div class="flex justify-between pt-3 border-t border-gray-200">
              <span class="text-lg font-semibold text-gray-900">Gesamt:</span>
              <span class="text-lg font-bold text-primary-600">
                {formatCurrency(zReportPreview.total_sales || 0)}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="flex justify-end gap-2">
        <button
          onclick={() => (showZReportCreate = false)}
          class="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
          disabled={isCreatingZReport}
        >
          Abbrechen
        </button>
        <button
          onclick={createZReport}
          class="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium"
          disabled={isCreatingZReport}
        >
          {#if isCreatingZReport}
            Wird erstellt...
          {:else}
            Z-Bericht erstellen & abschließen
          {/if}
        </button>
      </div>
    </div>
  </div>
{/if}
